import { Box, Paper, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useInsightsDashboard } from '../../../contexts/InsightsDashboardContext';
import type { StrategicInsightsData } from '../../../services/tenderAIService';

const EMPTY_OBJECT: Record<string, never> = {};

interface ValueMetricsProps {
  data: StrategicInsightsData | null;
  sourceLabels?: Record<string, string>;
}

const ValueMetrics = ({ data, sourceLabels = {} }: ValueMetricsProps) => {
  const theme = useTheme();
  const { state, dispatch } = useInsightsDashboard();

  if (!data?.aggregations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">No data available for the selected filters.</Typography>
      </Box>
    );
  }

  const { sector, client, source, team } = data.aggregations;

  const formatCurrency = (value: number | null) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatLongLabel = (value: string) => {
    return value.length > 25 ? `${value.substring(0, 22)}...` : value;
  };

  // Transform data for PieChart (Sector)
  const sectorData = Object.entries(sector || EMPTY_OBJECT)
    .filter(([_, agg]: [string, { totalEstimatedValue: number }]) => agg.totalEstimatedValue > 0)
    .map(([key, agg], idx) => ({
      id: idx,
      value: agg.totalEstimatedValue,
      label: key,
    }));

  // Transform data for PieChart (Source)
  const sourceData = Object.entries(source || EMPTY_OBJECT)
    .filter(([_, agg]: [string, { totalEstimatedValue: number }]) => agg.totalEstimatedValue > 0)
    .map(([key, agg], idx) => ({
      id: idx,
      value: agg.totalEstimatedValue,
      label: sourceLabels[key] || key,
      rawLabel: key,
    }));

  // Transform data for BarChart (Client)
  const clientEntries = Object.entries(client || EMPTY_OBJECT).filter(
    ([_, agg]: [string, { totalEstimatedValue: number }]) => agg.totalEstimatedValue > 0
  );
  const clientLabels = clientEntries.map(([key]) => formatLongLabel(key));
  const clientRawLabels = clientEntries.map(([key]) => key);
  const clientValues = clientEntries.map(
    ([_, agg]: [string, { totalEstimatedValue: number }]) => agg.totalEstimatedValue
  );

  // Transform data for BarChart (Team)
  const teamEntries = Object.entries(team || EMPTY_OBJECT).filter(
    ([_, agg]: [string, { totalEstimatedValue: number }]) => agg.totalEstimatedValue > 0
  );
  const teamLabels = teamEntries.map(([key]) => formatLongLabel(key));
  const teamRawLabels = teamEntries.map(([key]) => key);
  const teamValues = teamEntries.map(
    ([_, agg]: [string, { totalEstimatedValue: number }]) => agg.totalEstimatedValue
  );

  const handleFilterToggle = (
    type: 'SET_SECTORS' | 'SET_CLIENTS' | 'SET_SOURCES' | 'SET_TEAMS',
    currentValues: string[],
    value: string
  ) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    dispatch({ type, payload: newValues });
  };

  const hasData =
    sectorData.length > 0 ||
    clientValues.length > 0 ||
    sourceData.length > 0 ||
    teamValues.length > 0;

  if (!hasData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">
          No value metrics to display for the selected filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 4,
        height: '100%',
      }}
    >
      {/* Sector Value (Pie) */}
      <Paper
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 250, p: 2 }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Total Value by Sector
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
          {sectorData.length > 0 ? (
            <PieChart
              series={[
                {
                  data: sectorData,
                  innerRadius: 30,
                  outerRadius: 80,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  highlightScope: { fade: 'global', highlight: 'item' },
                  faded: { innerRadius: 20, additionalRadius: -20, color: 'gray' },
                  valueFormatter: (item) => formatCurrency(item.value),
                  arcLabel: (item) => (item.value >= 1000 ? formatCurrency(item.value) : ''),
                },
              ]}
              margin={{ right: 150 }}
              slotProps={{
                legend: { position: { vertical: 'middle', horizontal: 'end' } },
              }}
              onItemClick={(_event, d) => {
                {
                  const item = d.dataIndex !== undefined ? sectorData[d.dataIndex] : undefined;
                  if (item) {
                    handleFilterToggle('SET_SECTORS', state.sectors, item.label);
                  }
                }
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No sector data
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Client Value (Bar) */}
      <Paper
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 300, p: 2 }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Total Value by Client
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
          {clientLabels.length > 0 ? (
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data: clientLabels,
                  label: 'Client',
                  height: 120,
                  tickLabelStyle: {
                    angle: 45,
                    fontSize: 11,
                  },
                  tickPlacement: 'middle',
                  tickLabelPlacement: 'tick',
                },
              ]}
              yAxis={[{ label: 'Estimated Value (USD)' }]}
              series={[
                {
                  id: 'clientValues',
                  data: clientValues,
                  valueFormatter: (value) => formatCurrency(value),
                  color: theme.palette.primary.main,
                  barLabel: 'value',
                },
              ]}
              margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
              sx={{
                '& .MuiBarLabel-series-clientValues': {
                  fill: theme.palette.primary.contrastText,
                  fontWeight: 500,
                },
              }}
              onItemClick={(_event, d) => {
                {
                  const item = d.dataIndex !== undefined ? clientRawLabels[d.dataIndex] : undefined;
                  if (item) {
                    handleFilterToggle('SET_CLIENTS', state.clients, item);
                  }
                }
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No client data
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Source Value (Pie) */}
      <Paper
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 250, p: 2 }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Total Value by Tender Website
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
          {sourceData.length > 0 ? (
            <PieChart
              series={[
                {
                  data: sourceData,
                  innerRadius: 30,
                  outerRadius: 80,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  highlightScope: { fade: 'global', highlight: 'item' },
                  faded: { innerRadius: 20, additionalRadius: -20, color: 'gray' },
                  valueFormatter: (item) => formatCurrency(item.value),
                  arcLabel: (item) => (item.value >= 1000 ? formatCurrency(item.value) : ''),
                },
              ]}
              margin={{ right: 150 }}
              slotProps={{
                legend: { position: { vertical: 'middle', horizontal: 'end' } },
              }}
              onItemClick={(_event, d) => {
                {
                  const item = d.dataIndex !== undefined ? sourceData[d.dataIndex] : undefined;
                  if (item) {
                    handleFilterToggle('SET_SOURCES', state.sources, item.rawLabel);
                  }
                }
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No source data
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Team Value (Bar) */}
      <Paper
        variant="outlined"
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 300, p: 2 }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Total Value by Team
        </Typography>
        <Box sx={{ flexGrow: 1, minHeight: 0, position: 'relative' }}>
          {teamLabels.length > 0 ? (
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data: teamLabels,
                  label: 'Team',
                  height: 120,
                  tickLabelStyle: {
                    angle: 45,
                    fontSize: 11,
                  },
                  tickPlacement: 'middle',
                  tickLabelPlacement: 'tick',
                },
              ]}
              yAxis={[{ label: 'Estimated Value (USD)' }]}
              series={[
                {
                  id: 'teamValues',
                  data: teamValues,
                  valueFormatter: (value) => formatCurrency(value),
                  color: theme.palette.secondary.main,
                  barLabel: 'value',
                },
              ]}
              margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
              sx={{
                '& .MuiBarLabel-series-teamValues': {
                  fill: theme.palette.secondary.contrastText,
                  fontWeight: 500,
                },
              }}
              onItemClick={(_event, d) => {
                {
                  const item = d.dataIndex !== undefined ? teamRawLabels[d.dataIndex] : undefined;
                  if (item) {
                    handleFilterToggle('SET_TEAMS', state.teams, item);
                  }
                }
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No team data
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ValueMetrics;
